import { Heart, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { DECOR_OPTIONS, WHATSAPP_NUMBER, formatBRL } from "../data/chalets";

export const DecorSection = () => {
    return (
        <section id="decoracao" data-testid="decor-section" className="py-16 lg:py-24 bg-[#1B231A]">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div>
                        <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#C89B6D] mb-4 flex items-center gap-2">
                            <Heart className="w-4 h-4" strokeWidth={1.5} />
                            Experiência especial
                        </p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
                            Decoração Romântica
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-white/80">
                            Surpreenda quem você ama com um cenário inesquecível preparado antes da sua chegada.
                            Escolha uma das opções no momento da reserva.
                        </p>
                        <div className="mt-10 space-y-6">
                            {DECOR_OPTIONS.map((opt, i) => (
                                <div
                                    key={opt.id}
                                    data-testid={`decor-info-${opt.id}`}
                                    className="border border-white/15 rounded-sm p-6 hover:border-[#C89B6D]/60 transition-colors duration-300"
                                >
                                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                                        <h3 className="text-xl font-bold text-white">
                                            Opção {i + 1} · {opt.name}
                                        </h3>
                                        <span className="text-xl font-bold text-[#C89B6D]">{formatBRL(opt.price)}</span>
                                    </div>
                                    <p className="mt-2 text-sm text-white/70">{opt.tagline}</p>
                                    <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
                                        {opt.items.map((item) => (
                                            <li key={item} className="text-xs text-white/70">
                                                · {item}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="mt-3 text-xs font-bold uppercase tracking-wider text-[#C89B6D]">
                                        {opt.ideal}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="relative">
                        <img
                            src="/images/decoracao-romantica.jpeg"
                            alt="Opções de decoração romântica"
                            className="w-full rounded-sm border border-white/10"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export const ContactSection = () => {
    return (
        <section id="contato" data-testid="contact-section" className="py-16 lg:py-24 bg-[#FDFBF7]">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12">
                <div>
                    <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#8B5A2B] mb-4">Contato & Localização</p>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1B231A]">
                        Venha viver essa experiência
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-[#3E4C3D]">
                        Estamos no Taquaral, em Ouro Preto — cercados pela mata e pela história de Minas Gerais.
                        Fale conosco pelo WhatsApp para tirar dúvidas e garantir a sua data.
                    </p>
                    <div className="mt-10 space-y-5">
                        <div className="flex items-start gap-4">
                            <MapPin className="w-5 h-5 text-[#1B4332] mt-0.5" strokeWidth={1.5} />
                            <p className="text-sm text-[#3E4C3D]" data-testid="contact-address">
                                Estrada de terra fazenda Taquaral - Taquaral,
                                <br />
                                Ouro Preto - MG, 35400-000
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <Phone className="w-5 h-5 text-[#1B4332]" strokeWidth={1.5} />
                            <p className="text-sm text-[#3E4C3D]" data-testid="contact-phone">(31) 92003-2936</p>
                        </div>
                        <div className="flex items-center gap-4">
                            <Mail className="w-5 h-5 text-[#1B4332]" strokeWidth={1.5} />
                            <p className="text-sm text-[#3E4C3D]" data-testid="contact-email">admraritychales@gmail.com</p>
                        </div>
                    </div>
                    <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Olá! Gostaria de fazer uma reserva na Rarity Chalés.")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-whatsapp-button"
                        className="mt-10 inline-flex items-center gap-2 bg-[#1B4332] hover:bg-[#2D6A4F] text-white font-bold text-sm px-8 py-4 rounded-full transition-colors duration-300"
                    >
                        <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                        Chamar no WhatsApp
                    </a>
                </div>
                <div className="rounded-sm overflow-hidden border border-[#1B4332]/10 min-h-[320px]">
                    <iframe
                        title="Mapa Rarity Chalés"
                        data-testid="contact-map"
                        src="https://www.google.com/maps?q=Taquaral,+Ouro+Preto+-+MG,+35400-000&hl=pt-BR&output=embed"
                        className="w-full h-full min-h-[320px]"
                        loading="lazy"
                    />
                </div>
            </div>
        </section>
    );
};

export const Footer = () => {
    return (
        <footer data-testid="site-footer" className="bg-[#1B231A] py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
                    <div>
                        <div className="flex items-center gap-4">
                            <img src="/images/logo.jpg" alt="Rarity Chalés" className="w-16 h-16 rounded-full object-cover" />
                            <p className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white">
                                Rarity Chalés
                            </p>
                        </div>
                        <p className="mt-6 text-sm text-white/60 max-w-md">
                            Chalés de luxo em meio à natureza de Ouro Preto, Minas Gerais.
                            Privacidade, conforto e momentos inesquecíveis.
                        </p>
                    </div>
                    <div className="text-sm text-white/60 space-y-2">
                        <p>Estrada de terra fazenda Taquaral - Taquaral, Ouro Preto - MG</p>
                        <p>WhatsApp: (31) 92003-2936</p>
                        <p>admraritychales@gmail.com</p>
                        <p>CNPJ: 51.162.150/0001-32</p>
                    </div>
                </div>
                <div className="mt-16 pt-8 border-t border-white/10 text-xs text-white/40">
                    © {new Date().getFullYear()} Rarity Chalés. Todos os direitos reservados.
                </div>
            </div>
        </footer>
    );
};
